import { useContext, useEffect, useState } from "react";
import Message from "../../components/ui/Message";
import { PriceChangeSettings } from "./PriceChangeSettings";
import { AuthContext } from "../../context/auth";
import { MerchantsContext } from "../../context/merchants/MerchantContext";
import { MerchantAccountsContext } from "../../context/merchantAccounts/MerchantAccountContext";
import { SWIFTPAY_BANK_ID } from "../../utils/appConfig";

const COUNTRY_COSTA_RICA = 1;
const COMPANY_TYPE_RESTAURANT = 1;

// Sin país se toma Costa Rica (comercios anteriores a Panamá)
const isCostaRicaMerchant = (merchant: any) =>
    (Number(merchant?.country_id) || COUNTRY_COSTA_RICA) === COUNTRY_COSTA_RICA;

// Cuentas de liquidación: solo comercios de Swiftpay en Costa Rica
const showsBankAccounts = (merchant: any) =>
    isCostaRicaMerchant(merchant) && Number(merchant?.bankId) === SWIFTPAY_BANK_ID;

const currencyConfig: Record<number, { label: string; short: string; badgeClass: string }> = {
    188: { label: 'Cuenta en colones', short: 'CRC', badgeClass: 'bg-primary-subtle text-primary-emphasis' },
    840: { label: 'Cuenta en dólares', short: 'USD', badgeClass: 'bg-success-subtle text-success-emphasis' },
};

const getCompanyType = (companyTypeId: number) => {
    switch (companyTypeId) {
        case 1:
            return "Restaurante";
        case 2:
            return "Comercio";
        case 3:
            return "Delivery";
        case 4:
            return "POS";
        default:
            return "";
    }
}

// Mi empresa: datos del comercio, su configuración (régimen simplificado solo Costa Rica, servicio a la mesa
// solo restaurantes) y las cuentas bancarias donde se liquidan sus pagos (solo Swiftpay en Costa Rica)
export const MerchantTopPage = () => {

    const { user } = useContext(AuthContext);
    const { getCurrentMerchant, loadMerchantIdTypes, configMerchant } = useContext(MerchantsContext);
    const { loadMerchantAccounts, merchantAccounts, loadCatalogs, banks, accountTypes } = useContext(MerchantAccountsContext);

    const [merchant, setMerchant] = useState<any>(null);
    const [idTypes, setIdTypes] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [copiedIban, setCopiedIban] = useState<string | null>(null);

    // Configuración: valores guardados y del formulario (para saber si hay cambios)
    const [saved, setSaved] = useState({ simplifiedRegime: false, tableService: false });
    const [simplifiedRegime, setSimplifiedRegime] = useState(false);
    const [tableService, setTableService] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const loadData = async () => {
        setIsLoading(true);
        setLoadError(false);
        try {
            // Catálogos y comercio en paralelo: si falla un catálogo la pantalla igual se muestra
            const current = await getCurrentMerchant();
            // Tipos de identificación del país del comercio
            const types = await loadMerchantIdTypes((current as any)?.country_id).catch(() => []);
            setIdTypes(types ?? []);

            if (!current) {
                setLoadError(true);
                return;
            }
            setMerchant(current);

            const initial = {
                simplifiedRegime: !!current.simplifiedRegime,
                tableService: current.companyTypeId == COMPANY_TYPE_RESTAURANT ? !!current.tableService : false,
            };
            setSaved(initial);
            setSimplifiedRegime(initial.simplifiedRegime);
            setTableService(initial.tableService);

            // Si fallan las cuentas o sus catálogos, los datos de la empresa igual se muestran
            if (showsBankAccounts(current)) {
                await Promise.all([
                    loadCatalogs().catch(() => undefined),
                    loadMerchantAccounts(current.id, 1).catch(() => undefined),
                ]);
            }
        } catch (e) {
            setLoadError(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const getIdTypeName = (idType: number) =>
        idTypes.find((t: any) => t.id == idType)?.description ?? "-";

    const getBankName = (bankCode: number) =>
        banks.find((b: any) => b.code == bankCode)?.description ?? "-";

    const getAccountTypeName = (accountTypeId: number) =>
        accountTypes?.find((t: any) => t.code == accountTypeId)?.description
        ?? (accountTypeId == 1 ? "Ahorros" : accountTypeId == 2 ? "Corriente" : "-");

    const handleCopy = async (iban: string) => {
        try {
            await navigator.clipboard.writeText(iban);
            setCopiedIban(iban);
            setTimeout(() => setCopiedIban(null), 1500);
        } catch (e) { }
    };

    const hasChanges = simplifiedRegime !== saved.simplifiedRegime || tableService !== saved.tableService;

    const handleSave = async () => {
        setIsSaving(true);
        const resp = await configMerchant({
            id: merchant.id,
            simplifiedRegime,
            tableService,
        });
        setIsSaving(false);

        if (resp == "success") {
            // La sesión también la usan otras pantallas (facturación, menú QR)
            if (user?.merchant) {
                user.merchant.simplifiedRegime = simplifiedRegime;
                user.merchant.tableService = tableService;
            }
            setSaved({ simplifiedRegime, tableService });
            setSuccessMessage("Configuración actualizada correctamente");
        } else {
            setErrorMessage(resp);
        }
    }

    const handleReset = () => {
        setSimplifiedRegime(saved.simplifiedRegime);
        setTableService(saved.tableService);
    }

    if (isLoading) {
        return (
            <div className="mt-5 container-fluid px-4 text-center py-5">
                <span className="spinner-border spinner-border-sm me-2" /> Cargando información de la empresa...
            </div>
        );
    }

    if (loadError || !merchant) {
        return (
            <div className="mt-5 container-fluid px-4" style={styles.container}>
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                        <i className="bi bi-exclamation-circle text-danger" style={{ fontSize: 32 }} />
                        <h6 className="mt-2 mb-1">No se pudo cargar la información de la empresa</h6>
                        <p className="text-muted small mb-3">Verifique su conexión e intente de nuevo.</p>
                        <button type="button" className="btn btn-primary" onClick={loadData}>
                            <i className="bi bi-arrow-clockwise me-1" /> Reintentar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const isCostaRica = isCostaRicaMerchant(merchant);
    const hasBankAccounts = showsBankAccounts(merchant);
    const isRestaurant = merchant.companyTypeId == COMPANY_TYPE_RESTAURANT;
    const hasSettings = isCostaRica || isRestaurant;
    const companyType = getCompanyType(merchant.companyTypeId);

    const infoFields = [
        { label: 'Identificación legal', value: merchant.legalId, icon: 'bi-person-vcard' },
        { label: 'Tipo de identificación', value: getIdTypeName(merchant.typeLegalId), icon: 'bi-card-text' },
        { label: 'Correo electrónico', value: merchant.paymentEmail || merchant.email, icon: 'bi-envelope' },
        { label: 'Teléfono', value: merchant.phone, icon: 'bi-telephone' },
        { label: 'Dirección', value: merchant.address, icon: 'bi-geo-alt' },
    ];

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Mi empresa</h3>
                    <p className="text-muted mb-0">Datos de su comercio y su configuración.</p>
                </div>
            </div>

            <div className="row g-3">
                {/* Datos generales */}
                <div className="col-12 col-lg-5">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center gap-3 mb-4">
                                <div style={styles.iconCircle}>
                                    <i className="bi bi-building" />
                                </div>
                                <div>
                                    <div className="text-muted small">Nombre comercial</div>
                                    <h5 className="mb-0">{merchant.name}</h5>
                                    {companyType && <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis mt-1">{companyType}</span>}
                                </div>
                            </div>

                            <ul className="list-unstyled mb-0">
                                {infoFields.map(field => (
                                    <li key={field.label} className="d-flex gap-3 mb-3">
                                        <i className={`bi ${field.icon} text-muted mt-1`} />
                                        <div>
                                            <div className="text-muted small">{field.label}</div>
                                            <div className="fw-medium text-break">{field.value || '-'}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-7 d-flex flex-column gap-3">
                    {/* Configuración */}
                    {hasSettings && (
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <h6 className="mb-3">Configuración</h6>

                                {isCostaRica && (
                                    <div className="border rounded p-3 mb-3">
                                        <div className="form-check form-switch d-flex justify-content-between align-items-center ps-0 mb-0">
                                            <label className="form-check-label" htmlFor="regimeSwitch">
                                                <div className="fw-medium">Régimen simplificado</div>
                                                <div className="text-muted small">Indique si el comercio está inscrito en el régimen de tributación simplificada.</div>
                                            </label>
                                            <input
                                                id="regimeSwitch"
                                                className="form-check-input ms-3 flex-shrink-0"
                                                type="checkbox"
                                                role="switch"
                                                style={styles.switch}
                                                checked={simplifiedRegime}
                                                onChange={(e) => setSimplifiedRegime(e.target.checked)}
                                            />
                                        </div>
                                    </div>
                                )}

                                {isRestaurant && (
                                    <div className="border rounded p-3 mb-3">
                                        <div className="form-check form-switch d-flex justify-content-between align-items-center ps-0 mb-0">
                                            <label className="form-check-label" htmlFor="tableServiceSwitch">
                                                <div className="fw-medium">Servicio a la mesa</div>
                                                <div className="text-muted small">Active esta opción si el restaurante atiende a sus clientes en la mesa.</div>
                                            </label>
                                            <input
                                                id="tableServiceSwitch"
                                                className="form-check-input ms-3 flex-shrink-0"
                                                type="checkbox"
                                                role="switch"
                                                style={styles.switch}
                                                checked={tableService}
                                                onChange={(e) => setTableService(e.target.checked)}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="d-flex justify-content-end align-items-center gap-2 pt-1">
                                    {hasChanges && <span className="text-muted small me-auto"><i className="bi bi-info-circle me-1" />Tiene cambios sin guardar.</span>}
                                    <button type="button" className="btn btn-outline-secondary" onClick={handleReset} disabled={!hasChanges || isSaving}>
                                        Descartar
                                    </button>
                                    <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!hasChanges || isSaving}>
                                        {isSaving
                                            ? <><span className="spinner-border spinner-border-sm me-1" /> Guardando...</>
                                            : <><i className="bi bi-check-lg me-1" /> Guardar cambios</>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Cuentas bancarias */}
                    {hasBankAccounts && (
                    <div className="card border-0 shadow-sm flex-grow-1">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Cuentas bancarias</h6>
                                <span className="badge rounded-pill bg-light text-muted border">{merchantAccounts?.length ?? 0}</span>
                            </div>

                            {merchantAccounts && merchantAccounts.length > 0 ? (
                                <div className="d-flex flex-column gap-3">
                                    {merchantAccounts.map((account: any) => {
                                        const currency = currencyConfig[account.currencyId] ?? { label: 'Cuenta', short: String(account.currencyId), badgeClass: 'bg-secondary-subtle text-secondary-emphasis' };
                                        return (
                                            <div key={`${account.merchantId}-${account.currencyId}`} className="border rounded p-3">
                                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className="bi bi-bank text-primary" />
                                                        <span className="fw-semibold">{currency.label}</span>
                                                        <span className={`badge rounded-pill ${currency.badgeClass}`}>{currency.short}</span>
                                                    </div>
                                                    <span className="text-muted small">{getAccountTypeName(account.accountTypeId)}</span>
                                                </div>

                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                    <span className="fw-medium text-break" style={{ fontFamily: 'monospace', fontSize: '1.05rem' }}>
                                                        {account.ibanAccount}
                                                    </span>
                                                    {account.ibanAccount && (
                                                        <button
                                                            type="button"
                                                            className={`btn btn-sm ${copiedIban === account.ibanAccount ? 'btn-success' : 'btn-outline-secondary'}`}
                                                            title="Copiar IBAN"
                                                            onClick={() => handleCopy(account.ibanAccount)}
                                                        >
                                                            <i className={`bi ${copiedIban === account.ibanAccount ? 'bi-check-lg' : 'bi-clipboard'}`} />
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="row small">
                                                    <div className="col-sm-6">
                                                        <span className="text-muted">Banco: </span>{getBankName(account.bankCode)}
                                                    </div>
                                                    <div className="col-sm-6">
                                                        <span className="text-muted">Nombre: </span>{account.accountName || '-'}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="bi bi-bank text-muted" style={{ fontSize: 28 }} />
                                    <div className="fw-medium mt-2">No hay cuentas bancarias configuradas</div>
                                    <div className="text-muted small">Este comercio aún no tiene cuentas registradas.</div>
                                </div>
                            )}
                        </div>
                    </div>
                    )}

                    {/* Cambios de precio en caja (todos los comercios) */}
                    <PriceChangeSettings merchant={merchant} configMerchant={configMerchant} />
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {errorMessage.length > 0 && (
                <Message type='error' message={errorMessage} closeDialog={() => setErrorMessage('')} handleOk={() => setErrorMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
            )}
            {successMessage.length > 0 && (
                <Message type='success' message={successMessage} title='Exitoso' closeDialog={() => setSuccessMessage('')} handleOk={() => setSuccessMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
            )}
        </div>
    );
};

const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    iconCircle: {
        width: 56,
        height: 56,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 24,
        backgroundColor: '#e7f1ff',
        color: '#0d6efd',
        flexShrink: 0,
    },
    switch: {
        width: '2.75em',
        height: '1.4em',
        cursor: 'pointer',
    },
};
